import { toUserMessage } from '../../lib/errors.js';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { cloudDownloadOutline } from 'ionicons/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase, SUPABASE_URL } from '../../lib/supabase.js';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '../../lib/adapters/share/index.js';

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') return reject(new Error('FileReader did not return a string'));
      // result is "data:application/pdf;base64,<payload>"; strip prefix
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

type ReportType = 'completo' | 'temperature' | 'pulizie' | 'allerte' | 'tracciabilita';

const REPORT_LABELS: Record<ReportType, string> = {
  completo: 'Report completo',
  temperature: 'Solo temperature',
  pulizie: 'Solo pulizie',
  allerte: 'Solo allerte sonde',
  tracciabilita: 'Solo tracciabilità lotti',
};

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function ReportsPage(): JSX.Element {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 86_400_000);
  const [from, setFrom] = useState(isoDay(monthAgo));
  const [to, setTo] = useState(isoDay(today));
  const [reportType, setReportType] = useState<ReportType>('completo');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  async function generate(): Promise<void> {
    if (!profile?.active_tenant_id) {
      setToast('Nessuna azienda attiva');
      return;
    }
    if (!profile.active_business_id) {
      setToast('Nessun locale attivo');
      return;
    }
    setBusy(true);
    try {
      const { data } = await getSupabase().auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Non autenticato');
      const res = await fetch(`${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/report-asl`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenant_id: profile.active_tenant_id,
          business_id: profile.active_business_id,
          period_start: `${from}T00:00:00Z`,
          period_end: `${to}T23:59:59Z`,
          report_type: reportType,
          notes: notes.trim() || undefined,
        }),
      });
      if (!res.ok) {
        let detail = '';
        try {
          const j = await res.clone().json();
          detail = j?.detail?.message ?? j?.error ?? '';
        } catch {
          detail = await res.text().catch(() => '');
        }
        throw new Error(`HTTP ${res.status}${detail ? ` — ${detail}` : ''}`);
      }
      const blob = await res.blob();
      const fileName =
        `trackeat-asl-${profile.active_business_id.slice(0, 8)}-${reportType}-${from}-${to}.pdf`;
      if (Capacitor.isNativePlatform()) {
        // Su Android/iOS, <a download> non funziona dentro la Capacitor WebView.
        // Scriviamo il PDF in Cache (sempre scrivibile, no permessi) e poi
        // apriamo lo Share sheet nativo: l'utente puo "Salva su Drive",
        // mandarlo via WhatsApp/email, o aprirlo in un PDF viewer.
        const base64 = await blobToBase64(blob);
        const written = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
          // No `encoding` → data is treated as base64-encoded bytes (binary).
          recursive: true,
        });
        await Share.share({
          title: 'Report HACCP',
          url: written.uri,
          dialogTitle: 'Salva o condividi il report',
        });
        setToast('Report pronto — scegli dove salvarlo');
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        setToast('Report scaricato');
      }
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>Report ASL</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding" style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <p>{t('common.date')}: periodo da esportare in PDF firmato.</p>
        <IonList inset>
          <IonItem>
            <IonSelect
              label="Tipo report"
              labelPlacement="stacked"
              value={reportType}
              onIonChange={(ev) => setReportType(ev.detail.value as ReportType)}
            >
              {(Object.keys(REPORT_LABELS) as ReportType[]).map((k) => (
                <IonSelectOption key={k} value={k}>{REPORT_LABELS[k]}</IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
          <IonItem>
            <IonInput
              type="date"
              label="Da"
              labelPlacement="stacked"
              value={from}
              onIonInput={(ev) => setFrom(String(ev.detail.value ?? ''))}
            />
          </IonItem>
          <IonItem>
            <IonInput
              type="date"
              label="A"
              labelPlacement="stacked"
              value={to}
              onIonInput={(ev) => setTo(String(ev.detail.value ?? ''))}
            />
          </IonItem>
          <IonItem>
            <IonLabel position="stacked">Note (opzionale)</IonLabel>
            <IonTextarea
              autoGrow
              rows={3}
              placeholder="Annotazioni che compariranno nell'intestazione del report"
              value={notes}
              onIonInput={(ev) => setNotes(String(ev.detail.value ?? ''))}
            />
          </IonItem>
        </IonList>
        <IonButton expand="block" disabled={busy} onClick={() => { void generate(); }}>
          {busy ? <IonSpinner name="dots" /> : 'Genera PDF'}
        </IonButton>
        <IonFab slot="fixed" vertical="bottom" horizontal="end">
          <IonFabButton aria-label="Genera e scarica" disabled={busy} onClick={() => { void generate(); }}>
            <IonIcon icon={cloudDownloadOutline} />
          </IonFabButton>
        </IonFab>
        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2000} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
